import { useRequestURL, useRoute } from '#app'
import { computed, type Ref } from 'vue'
import { useHead } from '@unhead/vue'
import type { PlanType } from '~/resources/graphql/graphql'
import { unsafe_facilityCreateContractLink } from '~/composables/routing'
import { useActiveFacility } from '~/composables/use-active-facility'

export function useCanonicalPath(canonicalPath: Readonly<Ref<string | null>>) {
    const requestUrl = useRequestURL()
    const canonicalHref = computed(() => {
        if (canonicalPath.value === null) {
            return null
        }

        // Search engines expect absolute canonical URLs, while the app's route
        // helpers intentionally return path-only links for in-app navigation.
        return new URL(canonicalPath.value, requestUrl.origin).toString()
    })

    useHead({
        link: computed(() => {
            if (canonicalHref.value === null) {
                return []
            }

            return [
                {
                    key: 'canonical',
                    rel: 'canonical',
                    href: canonicalHref.value
                }
            ]
        })
    })

    return canonicalHref
}

export function useCanonicalPurchaseLink(
    planType: PlanType,
    slug: Readonly<Ref<unknown>>
) {
    const route = useRoute()
    const activeFacility = useActiveFacility()
    const canonicalPath = computed(() => {
        if (typeof slug.value !== 'string') {
            return null
        }

        // Canonical links should point to the regular storefront purchase URL,
        // without lightbox, kiosk, group, or query-parameter variants.
        return unsafe_facilityCreateContractLink(
            canonicalStorefrontPrefix(
                activeFacility.value.slug,
                route.params.mode,
                route.params.storefront
            ),
            planType,
            slug.value
        )
    })

    return useCanonicalPath(canonicalPath)
}

function canonicalStorefrontPrefix(
    facilitySlug: string,
    mode: unknown,
    storefront: unknown
): string {
    if (typeof mode === 'string' && mode.startsWith('kiosk-')) {
        return `/${facilitySlug}/${mode.substring(6)}`
    }

    if (typeof storefront === 'string' && storefront !== '') {
        return `/${facilitySlug}/${storefront}`
    }

    return `/${facilitySlug}`
}
